import React, {useEffect, useRef} from "react";
import { css } from "aphrodite";
import { CommonLayout } from "../../common/components/CommonLayout";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import cancelAppointmentIcon from "../../assets/icons/cancelAppointmentIcon.svg";
import { CommonLayoutTitle } from "../../common/components/CommonLayoutTitle";
import { RequestLayout } from "../../common/components/RequestLayout";
import { CustomMainButton } from "../../common/components/CustomMainButton";
import { useNavigate } from "react-router";
import { RoutePath } from "../../../Utils/routePath";
import { useSelector } from "react-redux";
import { Appointment, Media } from "../../../Models";
import { CompleteCommonMobileLayout } from "../../common/components/CompleteCommonMobileLayout";
import { MobileTitle } from "../../common/components/MobileTitle";
import cancelAppointmentMobile from "../../assets/icons/cancelAppointmentMobileIcon.svg";
import OtpModal from "../../common/components/OTPDialog/OTPModal";
import FriendlyCaptcha from "../../common/components/FriendlyCaptcha/FriendlyCaptcha";

const CancelAppointmentPage = () => {
    const [t] = useTranslation(["translation"]);
    const navigate = useNavigate();
    const cancelLoading = useSelector(Appointment.getCancelAppointmentLoading);
    const cancelError = useSelector(Appointment.getCancelAppointmentError);
    const appointmentInfoData = useSelector(Appointment.getAppointmentInfoData);
    const branchesData = useSelector(Appointment.getBranchesData);
    const cancelData = useSelector(Appointment.getCancelAppointmentData);
    const selectedNumber = useSelector(Appointment.getSelectedNumber);
    const selectedEmail = useSelector(Appointment.getSelectedEmail);
    const webView = useSelector(Media.getIsWebView);
    const mfaVerificationToken = useSelector(Appointment.getMfaVerificationToken);
    const loadingMfa = useSelector(Appointment.getMfaIsLoading);
    const errorMfa = useSelector(Appointment.getMfaError);
    const [otpModalVisible, setOtpModalVisible] = React.useState(false);
    const [captcha, setCaptcha] = React.useState(null);
    const friendlyCaptchaRef = React.useRef(null);
    const [pendingResendOtp, setPendingResendOtp] = React.useState(0);
    const lastCaptcha = useRef(null);

    useEffect(() => {
        if (!appointmentInfoData?.id) {
            navigate(RoutePath.findYourAppointmentPage, { replace: true });
        }
    }, [appointmentInfoData, navigate]);

    // Fetch branches if not already loaded
    useEffect(() => {
        if (!branchesData || branchesData.length === 0) {
            const serviceId = appointmentInfoData?.services?.[0];
            Appointment.fetchBranches(serviceId);
        }
    }, [branchesData, appointmentInfoData]);

    const appointmentBranch = branchesData?.find(branch => branch.id === appointmentInfoData?.branchId);

    const formattedAppointmentData = appointmentInfoData ? {
        bookingCode: appointmentInfoData.eventNumber,
        branch: appointmentBranch,
        start: appointmentInfoData.start,
    } : null;

    useEffect(() => {
        if (lastCaptcha.current !== captcha) {
            lastCaptcha.current = captcha;
            if (pendingResendOtp && captcha ) {
                handleResendOTP()
            }
        }

    }, [captcha, pendingResendOtp]);

    useEffect(() => {
        if (mfaVerificationToken) {
            setOtpModalVisible(true);
        }
    }, [mfaVerificationToken, navigate]);

    useEffect(() => {
        if (cancelData) {
            navigate(RoutePath.cancelAppointmentCompletedPage);
        }
    }, [cancelData, navigate]);

    const handleResendOTP = () => {
        setPendingResendOtp(0);
        sendOtp()
    }
    const triggerResendOtp = () => {
        setPendingResendOtp(Date.now());
        friendlyCaptchaRef.current?.reset();
    }
    const sendOtp = () => {
        Appointment.requestMfaSend({
            PhoneNumber: selectedNumber,
            Purpose: 1,
            CaptchaResponse: captcha,
            Email: selectedEmail
        });
    };

    const handleCancelAppointment = (otp) => {
        const params = {
            guests: appointmentInfoData.guests,
            Validations: [
                {
                    Token: otp,
                    VerificationToken: mfaVerificationToken,
                },
            ],
        }
        if (appointmentInfoData?.id) {
            Appointment.requestCancelAppointment(appointmentInfoData.id, params);
        }
    };

    const handleGoHome = () => {
        navigate(RoutePath.appointmentPage);
    };

    const handleNavigateToFindAppointment = () => {
        Appointment.resetState();
        navigate(RoutePath.findYourAppointmentPage);
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleCloseOtpModal = () => {
        setOtpModalVisible(false);
        setCaptcha(null);
        friendlyCaptchaRef.current?.reset();
    };

    const handleCaptchaSuccess = (solution) => {
        setCaptcha(solution);
    }
    const handleCaptchaFailure = () => {
        setCaptcha(null);
    }
    const handleCaptchaExpire = () => {
        setCaptcha(null);
        friendlyCaptchaRef.current?.reset();
    }

    return webView ? (
        <CommonLayout>
            <CommonLayoutTitle
                statusTitle={t("Find Appointment")}
                statusSubtitle={t("Cancel Appointment")}
                iconSrc={cancelAppointmentIcon}
                title={t("Cancel Appointment")}
                description={t(
                    "Are you certain you wish to cancel your appointment?"
                )}
                stepDataLayoutVisible={true}
                appointmentData={formattedAppointmentData}
                onStatusTitleClick={handleNavigateToFindAppointment}
            />
            <RequestLayout
                loading={loadingMfa && !otpModalVisible}
                error={cancelError || errorMfa}
                overlayLoading
            >

                <div className={css(styles.container)}>
                    <div className={css(styles.captchaContainer)}>
                        <FriendlyCaptcha
                            ref={friendlyCaptchaRef}
                            onSuccess={handleCaptchaSuccess}
                            onFailure={handleCaptchaFailure}
                            onExpire={handleCaptchaExpire}
                        />
                    </div>
                    <div className={css(styles.buttonContainer)}>
                        <CustomMainButton
                            color="error"
                            style={styles.button}
                            value={t("Cancel Appointment")}
                            isDisabled={!captcha}
                            onClick={sendOtp}
                        />
                        <CustomMainButton
                            color="primary"
                            style={styles.button}
                            value={t("Back to Home")}
                            onClick={handleGoHome}
                        />
                    </div>
                </div>
            </RequestLayout>
            <OtpModal
                isLoading={cancelLoading || loadingMfa}
                visible={otpModalVisible}
                onValidateOtp={handleCancelAppointment}
                onClose={handleCloseOtpModal}
                handleReturnToHome={handleGoHome}
                handleResendOtp={triggerResendOtp}
                validateButtonText={t("Confirm")}
                phoneOrEmail={selectedNumber}
                maxTries={4}
                timer={120}
            />
        </CommonLayout>
    ) : (
        <CommonLayout>
            <RequestLayout loading={loadingMfa && !otpModalVisible}
                error={cancelError || errorMfa}>
                <MobileTitle
                    iconSrc={cancelAppointmentMobile}
                    title={t("Cancel Appointment")}
                    onClickBackButton={handleGoBack}
                />
                <CompleteCommonMobileLayout
                    description={t(
                        "Are you certain you wish to cancel your appointment?"
                    )}
                    setCaptcha={setCaptcha}
                    appointmentValuesVisible={true}
                    appointmentData={formattedAppointmentData}
                    handleGoHome={handleGoHome}
                    secondaryButtonColor="error"
                    secondaryButtonOnClick={sendOtp}
                    secondaryButtonTitle={t("Cancel Appointment")}
                    secondaryButtonVisible={true}
                />
            </RequestLayout>
             <OtpModal
                isLoading={cancelLoading || loadingMfa}
                visible={otpModalVisible}
                onValidateOtp={handleCancelAppointment}
                onClose={handleCloseOtpModal}
                // handleReturnToHome={handleGoHome}
                handleResendOtp={triggerResendOtp}
                validateButtonText={t("Confirm")}
                phoneOrEmail={selectedNumber}
                maxTries={4}
                timer={120}
            />
        </CommonLayout>
    );
};

export default CancelAppointmentPage;
